/**
 * OpenClaw user-settings tab shell (Phase 12 of `lexide-implementation-
 * plan.md`; LexIDE side of the OpenClaw P9.A handoff —
 * `ai-docs/handoffs/lexide-openclaw-settings-handoff.md`).
 *
 * Wraps the 8 documented tabs (Agent · Personal · Channels · Notifications ·
 * Cron · Voice · Browser · Memory & Tools) in a single shell that:
 *
 *  - Fetches the merged effective view from `GET /api/lx/openclaw/config/get`
 *    on mount + on `refresh()` after every successful write. No client-side
 *    cache (handoff §2: org-admin kill-switch flips must propagate within
 *    one focus cycle, not after a page reload).
 *  - RBAC-filters the tab strip: MEMBER sees 6 (Personal first); ORG_ADMIN
 *    sees 8 (Agent first). Sourced from `useAuthIdentity().identity.
 *    isOrgAdmin` (Clerk-side org-role surfaced on the Lexiun JWT scopes).
 *  - Passes the loaded `config` + `refresh` down to each tab through
 *    `OpenclawSettingsContext` so individual tabs don't repeat the fetch.
 *
 * Slice 1 wires the Personal tab as the canonical implementation pattern;
 * the other tabs render `<PlaceholderTab/>` until subsequent slices fill
 * them in. The placeholder explicitly labels in-flight slices vs
 * deferred-on-runtime (P3.A.3.b channels / P4.D noVNC / P5.A.2.b real-
 * VAPID / P6 entire-cron) so an early user understands why a button is
 * inert.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  Bot,
  Brain,
  Clock,
  Mic,
  User,
  Loader2,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { AgentIncognitoIcon } from "@/components/icons/AgentIncognitoIcon";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useUIStore } from "@/stores/useUIStore";
import { useRuntimeVersion } from "@/hooks/useRuntimeVersion";
import {
  getOpenclawConfig,
  type OpenclawConfig,
} from "@/api/lx-openclaw-config";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { cn } from "@/lib/utils";
import { AgentTab } from "./AgentTab";
import { MemoryToolsTab } from "./MemoryToolsTab";
import { NotificationsTab } from "./NotificationsTab";
import { PersonalTab } from "./PersonalTab";
import { VoiceTab } from "./VoiceTab";

// ─── Tab catalog ──────────────────────────────────────────────────────

export type OpenclawTabId =
  | "agent"
  | "personal"
  | "identities"
  | "channels"
  | "notifications"
  | "cron"
  | "voice"
  | "browser"
  | "memory";

interface TabDef {
  id: OpenclawTabId;
  /** i18n key under `settings.openclaw.tabs.*` */
  labelKey: string;
  icon: LucideIcon;
  /** ORG_ADMIN-only tabs are filtered out for MEMBER. */
  orgAdminOnly?: boolean;
}

/** Order matters: this is the on-screen left-to-right order. ORG_ADMIN
 *  tabs (Agent, Memory & Tools) bookend the row so MEMBER's view
 *  (filtered) reads naturally Personal → Channels → … → Voice.
 *
 *  Option B (2026-05-31) hid the **Browser** tab from the lawyer surface.
 *  The runtime-side multi-profile machinery stays fully wired; the
 *  `/agent-browser` sidebar item now auto-connects to the auto-created
 *  default profile. `BrowserTab.tsx` is preserved as dead code so a
 *  beta-surfaced multi-profile need can re-enable it with a single
 *  entry here. Source of truth: lexruntime-openclaw-settings-implementation-
 *  plan.md §4-sexies "Option B" banner. `"browser"` stays in the union
 *  so dead-code BrowserTab.tsx still compiles. */
const TABS: readonly TabDef[] = [
  { id: "agent", labelKey: "settings.openclaw.tabs.agent", icon: AgentIncognitoIcon, orgAdminOnly: true },
  { id: "personal", labelKey: "settings.openclaw.tabs.personal", icon: User },
  // { id: "identities", labelKey: "settings.openclaw.tabs.identities", icon: AtSign },  // RETIRED 2026-07-27 — the hero business card (contacts + copy) replaced it; union keeps "identities" so stray refs compile
  // { id: "channels", labelKey: "settings.openclaw.tabs.channels", icon: MessagesSquare },  // RETIRED 2026-07-27 (assistant-hub S2d) — connect/details/reveal/test/rotate/disconnect live on the hero card chips + ChannelSettingsDialog; union keeps "channels" so stray refs compile
  { id: "notifications", labelKey: "settings.openclaw.tabs.notifications", icon: Bell },
  // { id: "cron", labelKey: "settings.openclaw.tabs.cron", icon: Clock },  // RETIRED 2026-07-27 (S-R2 P4) — SUPERSEDED by the Agendamentos rail pane. This tab wrote the P6-era RAW-CRON store (settings.json cron.jobs[]), a second write path bypassing the registry's locked rows, the fail-closed guard and the 30-day log. SAFE per the recurring-jobs P0 ruling 2: separate files, no clobber, 0 legacy jobs fleet-wide. `?tab=cron` now redirects to the pane (§4.5); union keeps "cron" so stray refs compile.
  { id: "voice", labelKey: "settings.openclaw.tabs.voice", icon: Mic },
  // { id: "browser", labelKey: "settings.openclaw.tabs.browser", icon: Chrome },  // HIDDEN — Option B 2026-05-31
  { id: "memory", labelKey: "settings.openclaw.tabs.memory", icon: Brain, orgAdminOnly: true },
];

// ─── Shared context ───────────────────────────────────────────────────

interface OpenclawSettingsContextValue {
  config: OpenclawConfig;
  /** Force a fresh `GET /config/get` (e.g. after a successful PATCH so
   *  kill-switch state propagates to siblings within the same shell). */
  refresh: () => Promise<void>;
  /** Reported by any tab when one of its fetches throws
   *  `LxOpenclawConfigError` with `routeNotFound: true` — i.e. the
   *  user's per-user lexruntime container is on an older image that
   *  doesn't carry that route. The shell shows the upgrade-required
   *  banner ONCE per occurrence and tabs MUST stop polling / retrying
   *  the offending endpoint until the user dismisses or retries
   *  (handoff §12-bis.4: "Don't poll-loop after an 'Upgrade required'
   *  404"). `tabId` is informational — the banner copy is generic. */
  notifyRouteNotFound: (tabId: OpenclawTabId) => void;
}

const OpenclawSettingsContext =
  createContext<OpenclawSettingsContextValue | null>(null);

/** Consumed by every tab to read the latest `org` + `user` view + to
 *  trigger a refresh after its own writes. Throws if used outside the
 *  shell — that would mean a tab is rendering without the loader, which
 *  would be a wiring bug. */
export function useOpenclawSettings(): OpenclawSettingsContextValue {
  const ctx = useContext(OpenclawSettingsContext);
  if (!ctx) {
    throw new Error(
      "useOpenclawSettings must be used inside <OpenclawSettingsTabs/>",
    );
  }
  return ctx;
}

/** Non-throwing variant — returns `null` outside the shell instead of raising.
 *  Lets a component that is normally hosted in the settings tabs (e.g.
 *  `TelegramSetupWizard`) ALSO be embedded elsewhere (the channel-connect
 *  dialog on the agent tab), where the loader/`refresh` don't exist and the
 *  caller supplies its own post-apply callback instead. Regular tabs keep
 *  using the throwing `useOpenclawSettings` so a genuine wiring bug still
 *  surfaces loudly. */
export function useOpenclawSettingsOptional(): OpenclawSettingsContextValue | null {
  return useContext(OpenclawSettingsContext);
}

// ─── The shell ────────────────────────────────────────────────────────

export function OpenclawSettingsTabs() {
  const { t } = useTranslation();
  const { identity } = useAuthIdentity();
  const isOrgAdmin = identity?.isOrgAdmin ?? false;

  // Filter the tab catalog by RBAC. The visible-tab list drives the
  // default-tab selection so MEMBER lands on Personal (first visible)
  // and ORG_ADMIN lands on Agent.
  const visibleTabs = useMemo(
    () => TABS.filter((t) => isOrgAdmin || !t.orgAdminOnly),
    [isOrgAdmin],
  );

  // Active tab is URL-driven via `?tab=<id>` so external surfaces (the
  // "Create a profile" CTA on `/agent-browser`, future deep-links, etc.)
  // can land directly on a specific tab. Falls back to the first visible
  // tab for MEMBER (`personal`) / ORG_ADMIN (`agent`) when `?tab` is
  // absent or names a hidden / unknown tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as OpenclawTabId | null;
  const activeTab: OpenclawTabId = useMemo(() => {
    if (tabFromUrl && visibleTabs.find((t) => t.id === tabFromUrl)) {
      return tabFromUrl;
    }
    return visibleTabs[0]?.id ?? "personal";
  }, [tabFromUrl, visibleTabs]);

  // §4.5 legacy alias: `?tab=cron` was the CronTab, retired in S-R2 P4 — its
  // destination is now the Agendamentos rail pane. Accepted forever inbound
  // (a bookmark, an older concierge answer) rather than silently falling
  // through to the first tab, which would strand the lawyer somewhere else.
  const openFocusPane = useUIStore((s) => s.openFocusPane);
  useEffect(() => {
    if (tabFromUrl !== "cron") return;
    openFocusPane("schedule");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      },
      { replace: true },
    );
  }, [tabFromUrl, openFocusPane, setSearchParams]);

  const setActiveTab = useCallback(
    (id: OpenclawTabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Fetch + refresh wiring ─────────────────────────────────────────
  const [config, setConfig] = useState<OpenclawConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Version-skew banner state (handoff §12-bis.4) ──────────────────
  // `routeNotFoundFrom` carries the tab that last hit a route-not-
  // found 404. Set once per occurrence; cleared on user retry/dismiss
  // OR when the user successfully refreshes. The banner stays visible
  // until cleared so the user always understands WHY a feature went
  // dead, not just "save failed".
  const [routeNotFoundFrom, setRouteNotFoundFrom] =
    useState<OpenclawTabId | null>(null);
  const notifyRouteNotFound = useCallback(
    (tabId: OpenclawTabId) => setRouteNotFoundFrom(tabId),
    [],
  );
  const dismissRouteNotFound = useCallback(
    () => setRouteNotFoundFrom(null),
    [],
  );

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const next = await getOpenclawConfig();
      setConfig(next);
      // A successful refresh implies the runtime IS reachable; clear
      // any stale "upgrade required" banner so the UI returns to a
      // clean state without the user clicking Dismiss.
      setRouteNotFoundFrom(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      throw err;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const next = await getOpenclawConfig();
        if (!cancelled) setConfig(next);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Render states ──────────────────────────────────────────────────
  if (loading) {
    return (
      <Card
        variant="surface"
        data-testid="openclaw-settings-loading"
        className="flex items-center gap-2 text-body-sm text-coffee-600 dark:text-cream-400"
      >
        <Loader2 size={14} className="animate-spin" />
        {t("settings.openclaw.loading")}
      </Card>
    );
  }

  if (error || !config) {
    return (
      <Card
        variant="surface"
        data-testid="openclaw-settings-error"
        className="flex flex-col gap-2 text-body-sm text-red-600 dark:text-red-400"
      >
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle size={14} />
          {t("settings.openclaw.loadFailed")}
        </div>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            void refresh().finally(() => setLoading(false));
          }}
          className="self-start rounded-md border border-cream-200/60 px-2.5 py-1 text-xs font-medium text-coffee-700 transition-colors hover:bg-cream-50 dark:border-coffee-700/60 dark:text-cream-200 dark:hover:bg-coffee-800/60"
        >
          {t("settings.openclaw.retry")}
        </button>
      </Card>
    );
  }

  const ctxValue: OpenclawSettingsContextValue = {
    config,
    refresh,
    notifyRouteNotFound,
  };

  return (
    <OpenclawSettingsContext.Provider value={ctxValue}>
      <div data-testid="openclaw-settings-shell" className="flex flex-col gap-4">
        <RuntimeBuildLine />
        {routeNotFoundFrom && (
          <UpgradeRequiredBanner
            onRetry={() => {
              setRouteNotFoundFrom(null);
              setLoading(true);
              void refresh().finally(() => setLoading(false));
            }}
            onDismiss={dismissRouteNotFound}
          />
        )}
        <nav
          data-testid="openclaw-settings-tabs"
          role="tablist"
          aria-label={t("settings.openclaw.tabsAriaLabel")}
          className="flex flex-wrap gap-2"
        >
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const selected = activeTab === tab.id;
            return (
              <Chip
                key={tab.id}
                role="tab"
                aria-selected={selected}
                aria-controls={`openclaw-tab-panel-${tab.id}`}
                active={selected}
                data-testid={`openclaw-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                leading={<Icon size={15} strokeWidth={1.75} />}
              >
                {t(tab.labelKey)}
              </Chip>
            );
          })}
        </nav>

        <div
          role="tabpanel"
          id={`openclaw-tab-panel-${activeTab}`}
          data-testid={`openclaw-tab-panel-${activeTab}`}
          className={cn("flex flex-col gap-4")}
        >
          {renderTabContent(activeTab)}
        </div>
      </div>
    </OpenclawSettingsContext.Provider>
  );
}

// ─── Tab body dispatcher ──────────────────────────────────────────────

function renderTabContent(tab: OpenclawTabId): ReactNode {
  switch (tab) {
    case "personal":
      return <PersonalTab />;
    case "agent":
      return <AgentTab />;
    case "notifications":
      return <NotificationsTab />;
    case "voice":
      return <VoiceTab />;
    // Option B 2026-05-31: Browser tab is hidden from the lawyer
    // surface; the runtime endpoints stay wired. /agent-browser
    // auto-connects to the auto-default profile instead.
    case "browser":
      return null;
    case "memory":
      return <MemoryToolsTab />;
    default:
      return null;
  }
}

// ─── Placeholder card ─────────────────────────────────────────────────

// ─── Runtime-build informational line (handoff §12-bis.4) ────────────

/** Tiny, READ-ONLY display of the per-user runtime's deployed image
 *  build + a relative "outdated" hint when the runtime is behind the
 *  published tag. Deliberately NOT interactive: the upgrade affordance
 *  is a PLATFORM_ADMIN operation in LexPortal (`/portal/runtimes` →
 *  "Upgrade all"); LexIDE only surfaces awareness of the skew, never
 *  the trigger (handoff §11 hard constraint #5). */
function RuntimeBuildLine() {
  const { t } = useTranslation();
  const { skew, loading } = useRuntimeVersion();
  if (loading || !skew) return null;
  const version = skew.running?.version;
  if (!version) return null;
  const outdated = skew.state === "behind";
  return (
    <p
      data-testid="openclaw-settings-runtime-build"
      className={cn(
        "text-caption",
        outdated
          ? "text-amber-700 dark:text-amber-400"
          : "text-coffee-500 dark:text-cream-500",
      )}
    >
      {t("settings.openclaw.runtimeBuild", { version })}
      {outdated && (
        <>
          {" "}
          <span
            data-testid="openclaw-settings-runtime-build-outdated"
            className="font-medium"
          >
            {t("settings.openclaw.runtimeBuildOutdated")}
          </span>
        </>
      )}
    </p>
  );
}

// ─── Upgrade-required banner (handoff §12-bis.4) ─────────────────────

/** Shell-level banner shown the moment any tab reports a
 *  route-not-found 404. The copy intentionally explains the situation
 *  in operator terms ("ask your platform administrator…") since LexIDE
 *  can't trigger the upgrade itself. "Retry" re-pulls the merged config
 *  view AND clears the banner if the refresh succeeds; "Dismiss" clears
 *  without re-fetching for users who want to keep working in the other
 *  (still-working) tabs. */
function UpgradeRequiredBanner({
  onRetry,
  onDismiss,
}: {
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="openclaw-settings-upgrade-required"
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-body-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <AlertTriangle size={14} className="shrink-0" />
      <span className="flex-1 min-w-0">
        {t("settings.openclaw.upgradeRequired.banner")}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="openclaw-settings-upgrade-required-retry"
          onClick={onRetry}
          className="rounded-md border border-amber-300/70 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-900/60 dark:bg-coffee-900 dark:text-amber-200 dark:hover:bg-amber-900/30"
        >
          {t("settings.openclaw.upgradeRequired.retry")}
        </button>
        <button
          type="button"
          data-testid="openclaw-settings-upgrade-required-dismiss"
          onClick={onDismiss}
          className="rounded-md px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30"
        >
          {t("settings.openclaw.upgradeRequired.dismiss")}
        </button>
      </div>
    </div>
  );
}

// `PlaceholderTab` lived here through Slices 1-7 as the fallback for
// tabs whose real component hadn't shipped yet (or whose runtime side
// was deferred). Slice 8 wired the last two cases (Agent + Memory &
// Tools), so the function and its two i18n keys (`placeholder.slice` +
// `placeholder.runtimeDeferred`) are now dead and have been removed.
// If a future tab needs to land as a stub, re-introduce the card in
// the slice that adds it — keeping it inline in the dispatcher avoids
// the orphan-code drift we hit between Slices 6 and 8.
